import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth, RegisterData } from '../../context/AuthContext';
import authService from '../../services/auth.service';

const Register: React.FC = () => {
  const navigate = useNavigate();
  
  // Form data state
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    fullname: '',
    phone: '',
    password: '',
    dob: '',
    gender: 'male'
  });
  
  // UI state
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Check password match when either password or confirm password changes
    if (name === 'password') {
      setPasswordsMatch(confirmPassword === '' || confirmPassword === value);
    }
  };
  
  // Handle confirm password change
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    setPasswordsMatch(e.target.value === formData.password);
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setRegistrationSuccess(false);
    
    // Validate form
    if (!formData.email || !formData.fullname || !formData.phone || !formData.password || !formData.dob) {
      setError('Vui lòng nhập đầy đủ thông tin');
      return;
    }
    
    // Validate phone number (simple Vietnamese phone format)
    const phoneRegex = /^(0|\+84)[3|5|7|8|9][0-9]{8}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError('Số điện thoại không hợp lệ');
      return;
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Email không hợp lệ');
      return;
    }
    
    // Check if passwords match
    if (formData.password !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      setPasswordsMatch(false);
      return;
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    setIsLoading(true);

    try {
      // Call the register API directly with authService
      const response = await authService.register(formData);
      
      if (response && response.id) {
        setRegistrationSuccess(true);
        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate('/auth/login', { 
            state: { 
              registrationSuccess: true,
              email: formData.email 
            } 
          });
        }, 2000);
      } else {
        setError('Đăng ký không thành công. Vui lòng thử lại sau.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Đã có lỗi xảy ra khi đăng ký. Vui lòng thử lại sau.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='max-w-md mx-auto my-12 px-4'>
      <div className='bg-white rounded-lg shadow-sm p-8'>
        <div className='text-center mb-8'>
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Đăng ký tài khoản</h1>
          <p className='text-gray-600'>
            Tạo tài khoản để mua sắm và theo dõi đơn hàng dễ dàng hơn
          </p>
        </div>

        {error && (
          <div className='bg-red-50 text-red-700 p-4 rounded-md mb-6'>
            {error}
          </div>
        )}

        {registrationSuccess && (
          <div className='bg-green-50 text-green-700 p-4 rounded-md mb-6'>
            Đăng ký thành công! Đang chuyển đến trang đăng nhập...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className='space-y-5'>
            <div>
              <label
                htmlFor='fullname'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Họ và tên <span className='text-red-500'>*</span>
              </label>
              <input
                id='fullname'
                name='fullname'
                type='text'
                value={formData.fullname}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                placeholder='Nhập họ và tên'
                required
              />
            </div>

            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Email <span className='text-red-500'>*</span>
              </label>
              <input
                id='email'
                name='email'
                type='email'
                value={formData.email}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                placeholder='Nhập địa chỉ email'
                required
              />
            </div>

            <div>
              <label
                htmlFor='phone'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Số điện thoại <span className='text-red-500'>*</span>
              </label>
              <input
                id='phone'
                name='phone'
                type='tel'
                value={formData.phone}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                placeholder='Nhập số điện thoại'
                required
              />
            </div>

            <div>
              <label
                htmlFor='password'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Mật khẩu <span className='text-red-500'>*</span>
              </label>
              <div className='relative'>
                <input
                  id='password'
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                  placeholder='Nhập mật khẩu (ít nhất 6 ký tự)'
                  required
                />
                <button
                  type='button'
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <EyeSlashIcon className='h-5 w-5' />
                  ) : (
                    <EyeIcon className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor='confirmPassword'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Xác nhận mật khẩu <span className='text-red-500'>*</span>
              </label>
              <div className='relative'>
                <input
                  id='confirmPassword'
                  name='confirmPassword'
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-primary focus:border-primary ${
                    !passwordsMatch && confirmPassword 
                      ? 'border-red-500' 
                      : 'border-gray-300'
                  }`}
                  placeholder='Nhập lại mật khẩu'
                  required
                />
                {!passwordsMatch && confirmPassword && (
                  <p className='text-red-500 text-xs mt-1'>
                    Mật khẩu không khớp
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor='dob'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Ngày sinh <span className='text-red-500'>*</span>
              </label>
              <input
                id='dob'
                name='dob'
                type='date'
                value={formData.dob}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              />
            </div>

            <div>
              <label
                htmlFor='gender'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Giới tính <span className='text-red-500'>*</span>
              </label>
              <select
                id='gender'
                name='gender'
                value={formData.gender}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              >
                <option value='male'>Nam</option>
                <option value='female'>Nữ</option>
                <option value='other'>Khác</option>
              </select>
            </div>

            <div>
              <button
                type='submit'
                disabled={isLoading || registrationSuccess}
                className='w-full bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-md transition duration-300 flex items-center justify-center disabled:bg-primary/70 disabled:cursor-not-allowed'
              >
                {isLoading ? (
                  <>
                    <svg
                      className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    Đang xử lý...
                  </>
                ) : registrationSuccess ? (
                  'Đăng ký thành công'
                ) : (
                  'Đăng ký'
                )}
              </button>
            </div>
          </div>
        </form>

        <div className='mt-8 text-center'>
          <p className='text-gray-600 text-sm'>
            Đã có tài khoản?{' '}
            <Link
              to='/auth/login'
              className='text-primary hover:text-primary-dark font-medium'
            >
              Đăng nhập ngay
            </Link>
          </p>
        </div>

        {/* Social login options */}
        <div className='mt-8'>
          <div className='relative'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-gray-300' />
            </div>
            <div className='relative flex justify-center text-sm'>
              <span className='px-2 bg-white text-gray-500'>
                Hoặc đăng ký với
              </span>
            </div>
          </div>

          <div className='mt-6 grid grid-cols-2 gap-3'>
            <button
              type='button'
              className='w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-center'
            >
              <svg
                className='w-5 h-5 mr-2'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M20.283 10.356h-8.327v3.451h4.792c-.446 2.193-2.313 3.453-4.792 3.453a5.27 5.27 0 0 1-5.279-5.28 5.27 5.27 0 0 1 5.279-5.279c1.259 0 2.397.447 3.29 1.178l2.6-2.599c-1.584-1.381-3.615-2.233-5.89-2.233a8.908 8.908 0 0 0-8.934 8.934 8.907 8.907 0 0 0 8.934 8.934c4.467 0 8.529-3.249 8.529-8.934 0-.528-.081-1.097-.202-1.625z'></path>
              </svg>
              Google
            </button>
            <button
              type='button'
              className='w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-center'
            >
              <svg
                className='w-5 h-5 mr-2'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z'></path>
              </svg>
              Facebook
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
